import dynamic from 'next/dynamic';
import { signOut, useSession } from 'next-auth/react';
import React, { useEffect, useMemo, useState } from 'react';

import useIdentifyMetrics from '../hooks/useIdentifyMetrics';
import useSignOut from '../hooks/useSignOut';
import BackendService from '../services/backendService';
import { getEndImpersonationCallbackUrl } from '../utils/endImpersonationRedirect';
import { configureStore } from '../utils/store';

const ImpersonationBanner = dynamic(
  () => import('../components/_organisms/ImpersonationBanner/ImpersonationBanner'),
  { ssr: false },
);

const MetricsIdentifier = ({ uid }: { uid: string }) => {
  useIdentifyMetrics(uid);
  return null;
};

const BackendServiceProvider = ({ children }: { children: React.ReactNode }) => {
  const session = useSession();
  const { b2cSignout } = useSignOut();
  const { config } = useMemo(() => configureStore(), []);

  useState(() => {
    new BackendService().init(config.backend.externalApiUrl, config.cdn.url);
    if (session && session.data?.idToken) {
      BackendService.instance.setToken(session.data.idToken as string);
    }
  });

  useEffect(() => {
    if (session && session.data?.idToken) {
      new BackendService();
      BackendService.instance.setToken(session.data.idToken as string);
    }
  }, [config.backend.externalApiUrl, config.cdn.url, session]);

  useEffect(() => {
    if (session?.data?.error === 'RefreshAccessTokenError') {
      if (session.data.isImpersonation) {
        signOut({ callbackUrl: getEndImpersonationCallbackUrl() });
      } else {
        // Federated sign-out: clears NextAuth session AND the B2C session cookie,
        // then lands on /login so the user can pick how to sign in. A plain
        // signIn() here would silently re-auth via SSO and leave them in the
        // same broken state.
        b2cSignout(`${window.location.origin}/login`);
      }
    }
  }, [session, b2cSignout]);

  return (
    <>
      {session?.data?.isImpersonation && <ImpersonationBanner />}
      {session?.data?.user?.uid && <MetricsIdentifier uid={session.data.user.uid} />}
      {children}
    </>
  );
};

export default BackendServiceProvider;
